import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import { FleetOpsGridColDef } from '../../types/agGrid';
import EntityButtons from 'components/EntityButtons';
import GridCellErrorBoundary from '../GridCellErrorBoundary';
import PerformanceBoardContexts from './contexts';
import Cell from '../V5Gadget/Matrix/Cell';
import Tooltip from '../Tooltip';
import BoardContext from 'contexts/BoardContext';
import isPerformanceBoard from 'isPerformanceBoard';
import RolesContext from 'contexts/RolesContext';
import FeatureGatesContext from 'contexts/FeatureGatesContext';
import PERFORMANCE_BOARD_PERMISSIONS from 'permissionDefinitions/performanceBoard';
import BonusOverrideIndicator, {
  BonusAdditionalPaymentMetaData,
  BonusExclusionMetaData,
  BonusOverrideMetaData,
} from './BonusOverrideIndicator';

const PerformanceEntityCell = (props: ICellRendererParams) => {
  const { onEntitySelected } = useContext(
    PerformanceBoardContexts.SelectedEntity,
  );
  const { currentPermissions } = useContext(RolesContext);
  const { board } = useContext(BoardContext);
  const { isAdditionalPaymentsEnabled } = useContext(FeatureGatesContext);
  const { value } = props;
  const [isRowHovered, setIsRowHovered] = useState<boolean>(false);

  const onMouseEnter = useCallback(() => setIsRowHovered(true), []);
  const onMouseLeave = useCallback(() => setIsRowHovered(false), []);

  useEffect(() => {
    props.node.addEventListener('mouseEnter', onMouseEnter);
    props.node.addEventListener('mouseLeave', onMouseLeave);
    return () => {
      props.node.removeEventListener('mouseEnter', onMouseEnter);
      props.node.removeEventListener('mouseLeave', onMouseLeave);
    };
  }, [props.node, onMouseEnter, onMouseLeave]);

  const entityDefinition = useMemo(() => {
    const colDef = props.colDef as FleetOpsGridColDef | null;
    return colDef?.cellRendererParams?.entityDefinition;
  }, [props.colDef]);

  const onClick = useCallback(() => {
    if (props.node.rowPinned) {
      return;
    }
    // The slideout opens over the row, so its mouseLeave never fires; drop
    // the hover state here or the row's link stays stuck on.
    setIsRowHovered(false);
    onEntitySelected(value);
  }, [onEntitySelected, props.node.rowPinned, value]);

  const field = useMemo(() => {
    if (props.colDef) {
      return props.colDef.field;
    }
    return undefined;
  }, [props.colDef]);

  const bonusOverridesMetaData = useMemo(():
    | undefined
    | BonusOverrideMetaData[] => {
    // Criteria/qualifier/reward overrides come pre-derived by the grid pipeline
    // as `_overrides` (reward entries use `type: 'reward'`). The override reason
    // codes are NOT on those entries — they live in a single row-level
    // `reasonCode` dataset column (recorded once for the whole override change),
    // so attach them to each entry for the tooltip to surface.
    const metaData: BonusOverrideMetaData[] | undefined =
      props.node.data['_overrides'];

    if (!metaData || metaData.length === 0) {
      return undefined;
    }

    const rowReasonCodes = props.node.data['reasonCode'];
    const reasonCodes: string[] | undefined = Array.isArray(rowReasonCodes)
      ? rowReasonCodes
      : undefined;

    if (!reasonCodes || reasonCodes.length === 0) {
      return metaData;
    }

    return metaData.map((override) => ({ ...override, reasonCodes }));
  }, [props.node.data]);

  const bonusExclusionsMetaData = useMemo(():
    | undefined
    | BonusExclusionMetaData[] => {
    const metaData: BonusExclusionMetaData[] | undefined =
      props.node.data['_exclusions'];

    if (metaData && metaData.length > 0) {
      return metaData;
    }

    return undefined;
  }, [props.node.data]);

  const bonusAdditionalPaymentsMetaData = useMemo(():
    | undefined
    | BonusAdditionalPaymentMetaData[] => {
    // Hidden until the account's additionalPayments gate is opened.
    if (!isAdditionalPaymentsEnabled) {
      return undefined;
    }
    // null once a record's last payment is removed; missing on records written
    // before recognition payments shipped.
    const metaData: BonusAdditionalPaymentMetaData[] | null | undefined =
      props.node.data['_additionalPayments'];

    if (metaData && metaData.length > 0) {
      return metaData;
    }

    return undefined;
  }, [isAdditionalPaymentsEnabled, props.node.data]);

  if (value === undefined || value === null) {
    return (
      <Cell>
        <span>-</span>
      </Cell>
    );
  }

  if (Array.isArray(value)) {
    const text = value.join(', ');
    const shouldTooltip = text.length && text.length > 14;
    return (
      <Cell>
        <Tooltip content={shouldTooltip ? text : undefined}>
          <span>{text}</span>
        </Tooltip>
      </Cell>
    );
  }

  const hasEditPermission = currentPermissions.includes(
    PERFORMANCE_BOARD_PERMISSIONS.MODIFY_WIDGETS,
  );

  const isLinkDisabled =
    (isPerformanceBoard(board) &&
      board.slideout.length === 0 &&
      !hasEditPermission) ||
    !!props.node.rowPinned;

  return (
    <Cell
      onClick={onClick}
      disableLink={isLinkDisabled}
      style={{ gap: 4, position: 'relative' }}
    >
      <span
        style={{
          color: isLinkDisabled ? undefined : 'var(--color-text-link)',
          // Truncate the name before the icons when the column is narrow
          // (e.g. while the slideout is open).
          minWidth: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
      <>
        {!!field &&
          field === 'driver' &&
          (!!bonusOverridesMetaData ||
            !!bonusExclusionsMetaData ||
            !!bonusAdditionalPaymentsMetaData) && (
            <BonusOverrideIndicator
              driver={value}
              bonusOverridesMetaData={bonusOverridesMetaData ?? []}
              bonusExclusionsMetaData={bonusExclusionsMetaData}
              bonusAdditionalPaymentsMetaData={bonusAdditionalPaymentsMetaData}
            />
          )}
      </>
      {isRowHovered && !props.node.rowPinned && !!entityDefinition && (
        // In the row's flow, at the end, rather than overlaid on the
        // right edge where it covered the bonus indicator in narrow columns.
        <div
          style={{ marginLeft: 'auto', flexShrink: 0, display: 'flex' }}
          onClick={(e) => e.stopPropagation()}
        >
          <EntityButtons.Link
            entity={entityDefinition.entity}
            entityPrimaryFieldValue={value}
            app={entityDefinition.app}
          />
        </div>
      )}
    </Cell>
  );
};

const Gate = (params: ICellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<PerformanceEntityCell {...params} />}
  />
);

export default Gate;
